<template>
  <div class="upload-section">
    <div class="upload-section__header">
      <span class="upload-section__limit-text">{{ maxPhotos }} {{ maxItemsWord }} max</span>
      <slot name="header-right">
        <LinkWithIcon isDottedUnderline @click="emit('close')">
          <template #icon><IconEmbedded name="remove_2" :size="13" /></template>
          <span>Close</span>
        </LinkWithIcon>
      </slot>
    </div>
    
    <!-- Hidden upload form (provides the file input) -->
    <div class="upload-section__hidden-form">
      <UploadPhotoForm
        :callerName="`photoUpload-${inputId}`"
        flow="social"
        :img="[]"
        :isVideoUploadAllowed="props.isVideoUploadAllowed"
        :isVideoOnlyUploadAllowed="props.isVideoOnlyUploadAllowed"
        :maxNumberOfFilesToUploadAtOnce="maxPhotos"
        :isThumbnailsVisible="false"
        hideVideoGuidance
        :inputId="inputId"
        @uploadingStarted="isUploadingInProgress = true"
        @imageUploadedToServer="handleImageUploaded"
        @uploadProgressChange="({ isUploadInProgress }) => { if (!isUploadInProgress) isUploadingInProgress = false; }"
      />
    </div>
    
    <!-- Visible upload button + thumbnails row -->
    <ul class="upload-section__thumbnails-list">
      <!-- Upload button -->
      <li v-if="uploadedAssetIds.length < maxPhotos" class="add-files-button upload-section__thumbnail">
        <label class="add-files-button__label" :for="inputId">
          <template v-if="isUploadingInProgress">
            <LoadingIcon class="add-files-button__icon" :size="32" color="rgba(0, 0, 0, 0.5)" />
            <div class="add-files-button__text">Uploading<EllipsisAnimated /></div>
          </template>
          <template v-else>
            <IconEmbedded class="add-files-button__icon" name="upload_2" :size="32" color="rgba(0, 0, 0, 0.5)" />
            <div class="add-files-button__text">Upload</div>
          </template>
        </label>
      </li>
      
      <!-- Uploaded photo thumbnails inline -->
      <li v-for="assetId in uploadedAssetIds" :key="assetId" class="upload-section__thumbnail">
        <SrpFileThumbnail
          :fileName="assetId"
          :galleryPostfix="`${inputId}-uploaded`"
          :isDeleteButton="false"
          isEntireAreaCheckable
        >
          <input
            type="checkbox"
            :checked="selectedAssetIds.includes(assetId)"
            :disabled="isAtLimit && !selectedAssetIds.includes(assetId)"
            @change="$event => handleCheckboxChange(($event.target as HTMLInputElement).checked, assetId)"
          />
        </SrpFileThumbnail>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

// Components
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import UploadPhotoForm from "@views/CMS/UploadPhotoForm.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";

// Types
import { UploadedImage } from "@contracts/uploadedImage";

// Constants
import { MAX_PHOTOS_PER_POST } from "@/constants/social";

const props = withDefaults(
  defineProps<{
    inputId: string;
    maxPhotos?: number;
    modelValue: string[];
    selectedAssetIds: string[];
    isVideoUploadAllowed?: boolean;
    isVideoOnlyUploadAllowed?: boolean;
  }>(),
  {
    maxPhotos: MAX_PHOTOS_PER_POST,
    isVideoUploadAllowed: false,
    isVideoOnlyUploadAllowed: false,
  }
);

const maxItemsWord = computed(() => {
  if (props.isVideoOnlyUploadAllowed) {
    return "videos";
  }
  if (props.isVideoUploadAllowed) {
    return "items";
  }
  return "photos";
});

const emit = defineEmits<{
  (e: "update:modelValue", assetIds: string[]): void;
  (e: "select", assetId: string): void;
  (e: "deselect", assetId: string): void;
  (e: "close"): void;
}>();

// Upload state
const isUploadingInProgress = ref<boolean>(false);

// Uploaded asset IDs (local copy of modelValue)
const uploadedAssetIds = computed({
  get: () => props.modelValue,
  set: (value: string[]) => emit("update:modelValue", value),
});

// Is at limit
const isAtLimit = computed(() => props.selectedAssetIds.length >= props.maxPhotos);

// Handle image uploaded
function handleImageUploaded(uploadedImage: UploadedImage) {
  uploadedAssetIds.value = [...uploadedAssetIds.value, uploadedImage.serverId];
}

// Handle checkbox change
function handleCheckboxChange(isSelected: boolean, assetId: string) {
  if (isSelected) {
    emit("select", assetId);
  } else {
    emit("deselect", assetId);
  }
}
</script>

<style scoped lang="scss">
@import "./photo-upload";
</style>
